/**
 * 入れ物のサイズを測る。
 *
 * **`getBoundingClientRect()` を使ってはいけない。** あれは CSS の
 * `transform` を掛けた**見た目の**大きさを返すため、**祖先に拡大が掛かって
 * いると入れ物が実際より大きいと誤認する。**
 *
 * S04 の作業画面はズームを親要素の `transform: scale()` で掛けている。
 * `getBoundingClientRect()` で測っていたため、**倍率 2 で拡大した状態で
 * オブジェクトを動かすと(`setTemplate` が `resize` を呼ぶ)ステージが
 * 2 倍に膨らみ、その上でさらに 2 倍に拡大されて画面が崩れていた**
 * (#116 の「拡大してからオブジェクトを操作すると大きく崩れる」)。
 *
 * `clientWidth` / `clientHeight` は**レイアウト上の**内容領域を返すので
 * `transform` の影響を受けない。整数に丸められるが、canvas の実ピクセルは
 * どうせ丸めるため実害はない。
 */
export function measure(container) {
    return { width: container.clientWidth, height: container.clientHeight };
}
/**
 * 入れ物のサイズとキャンバスのアスペクト比から表示領域を決める。
 *
 * `contain` は全体が入るように余白を作り、`cover` は入れ物を埋めて
 * はみ出す分を切る。**どちらもアスペクト比は保つ** —— 崩すと
 * 「社内で確認した見た目と外部表示が一致する」(設計書 7.4)が壊れる。
 */
export function computeTransform(container, canvas, fit = 'contain') {
    // 0 除算とマイナスを避ける。入れ物が未レイアウトのときに起きる。
    if (container.width <= 0 || container.height <= 0 || canvas.width <= 0 || canvas.height <= 0) {
        return { width: 0, height: 0, offsetX: 0, offsetY: 0 };
    }
    const containerRatio = container.width / container.height;
    const canvasRatio = canvas.width / canvas.height;
    // contain は「入れ物より横長なら幅に合わせる」、cover はその逆。
    const matchWidth = fit === 'contain' ? canvasRatio > containerRatio : canvasRatio < containerRatio;
    const width = matchWidth ? container.width : container.height * canvasRatio;
    const height = matchWidth ? container.width / canvasRatio : container.height;
    return {
        width,
        height,
        offsetX: (container.width - width) / 2,
        offsetY: (container.height - height) / 2,
    };
}
/**
 * 正規化座標を表示座標(ステージ内の px)に変換する。
 *
 * **オフセットは足さない。** ステージ要素自体が中央寄せされる前提で、
 * ステージのローカル座標を返す —— canvas の座標系と一致させるため。
 */
export function toStage(point, transform) {
    return [point[0] * transform.width, point[1] * transform.height];
}
/** 頂点の配列を表示座標へ。 */
export function quadToStage(points, transform) {
    return points.map((p) => toStage(p, transform));
}
/**
 * `clip-path: polygon(...)` の値を作る。
 *
 * 障害物マスクは **DOM 側の `clip-path`** で実現する(PoC 3.6 で成立を確認)。
 * WebGL を採用してもここは DOM のままでよい —— canvas の上に重ねる形で
 * 遮蔽が働く。
 */
export function toClipPath(points, transform) {
    const pairs = points
        .map((p) => {
        const [x, y] = toStage(p, transform);
        return `${round(x)}px ${round(y)}px`;
    })
        .join(', ');
    return `polygon(${pairs})`;
}
/** CSS に出す座標。小数第 2 位で十分(サブピクセルの差は見えない)。 */
function round(value) {
    return Math.round(value * 100) / 100;
}
/**
 * **輪郭をぼかしたマスク画像**をデータ URI で作る(#125 / [ADR-0034](../../../docs/adr/0034-obstacle-edge-blur.md))。
 *
 * 返すのは `mask-image` に渡す値。**白いポリゴン 1 枚に `feGaussianBlur`** を
 * かけただけの SVG で、これがマスクのアルファになる。
 *
 * ## なぜ「マスク」なのか —— `filter: blur()` ではいけない理由
 *
 * `filter: blur()` は要素のレンダリング結果を**色とアルファまとめて**畳み込む。
 * 切り抜きの内側は完全不透明なので、そこでは**背景画像の画素どうしが混ざる**
 * —— 柱のテクスチャがぼける。**ぼかしたいのは輪郭だけ。**
 *
 * マスクなら要素は**マスクされる側**なので、画像そのものには何も起きない。
 * ぼけるのはアルファだけ —— **要件が方式で保証される。**
 *
 * ## なぜ DOM の `<svg>` を `url(#id)` で参照しないのか
 *
 * ビューアは Shadow DOM の中にいて([ADR-0017](../../../docs/adr/0017-viewer-embed-boundary.md))、
 * **Shadow root をまたぐ `url(#id)` の解決は実装差の事故が多い。** S04 は
 * 通常 DOM で、**1 ページに複数のマスクが並ぶ**ので id の衝突も抱える。
 *
 * データ URI なら **SVG が独立したドキュメントとして閉じる** ——
 * ツリースコープにも id 衝突にも原理的に関わらない。
 *
 * `stdDeviation` は**表示 px**。呼び出し側がキャンバス幅比から換算する。
 */
export function toBlurMask(points, transform, stdDeviation) {
    const width = round(transform.width);
    const height = round(transform.height);
    const pairs = points
        .map((p) => {
        const [x, y] = toStage(p, transform);
        return `${round(x)},${round(y)}`;
    })
        .join(' ');
    /*
     * **フィルタ領域は SVG 全体**(`userSpaceOnUse`)。
     *
     * 既定の `objectBoundingBox` は**ポリゴンの外接矩形に対する比率**なので、
     * 細長い障害物では余白が足りず**ぼけが途中で切り落とされる。**
     * マスク画像は SVG のサイズで閉じるのだから、そこまで広げれば十分。
     */
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">` +
        `<filter id="b" filterUnits="userSpaceOnUse" x="0" y="0" width="${width}" height="${height}">` +
        `<feGaussianBlur stdDeviation="${round(stdDeviation)}"/>` +
        `</filter>` +
        `<polygon points="${pairs}" fill="#fff" filter="url(#b)"/>` +
        `</svg>`;
    return `data:image/svg+xml,${encodeSvg(svg)}`;
}
/**
 * SVG をデータ URI に埋め込める形にする。
 *
 * **`(` と `)` も潰す。** `encodeURIComponent` はこの 2 つを残すが、
 * SVG の中には `filter="url(#b)"` があり、**素通りすると `url()` が早く閉じる**
 * (背景 URL に対する [`cssUrl`](./stage.ts) と同じ問題)。
 *
 * `"` `<` `>` `#` は `encodeURIComponent` が処理する —— `#` を残すと
 * URI の断片識別子と解釈され、**SVG がそこで切れる。**
 */
function encodeSvg(svg) {
    return encodeURIComponent(svg).replace(/\(/g, '%28').replace(/\)/g, '%29');
}
/**
 * 重なり順で並べる(`order` が小さいほど下 — F-10)。
 *
 * **元の配列を破壊しない。** 呼び出し側が保持している配列を並べ替えると、
 * レイヤーパネルの表示順(#36)が勝手に変わる。
 */
export function byOrder(items) {
    return [...items].sort((a, b) => a.order - b.order);
}
//# sourceMappingURL=layout.js.map